import { useEffect } from "react";
import { Box, Button, Typography, Paper, List, ListItemButton, ListItemText } from "@mui/material";
import "./InfoResultBlock.css";
import {
  CoproPortfolio,
  CoproPortfolioOpenRequest,
  asPortfolioText,
  coprosGereesLabel,
  formatSiren,
  isEtablissementSiege,
  portfolioContextFromFiche,
} from "./coproPortfolio";

export const COPRO_TAB_INDEX = 1;

/** Réponse API place-copro simplifiée : parcelle + liste de fiches copro */
export interface PlaceCoproFicheSimple {
  [key: string]: unknown;
  parcelles_cadastre?: Array<{ id: string; geometry?: unknown; type?: string; properties?: unknown }>;
  /** Portefeuille syndic (première page) déjà agrégé par GET /api/place-copro. */
  portfolio?: CoproPortfolio;
}

/** Feature GeoJSON pour nearbyCopro (geometry = distance.location, properties = fiche + distance) */
export interface PlaceCoproNearbyFeature {
  type: "Feature";
  geometry: unknown;
  properties: Record<string, unknown> & {
    distance?: { calculated?: number; location?: unknown };
  };
}

/** Feature GeoJSON groupe de bâtiment (geometry + properties surface, hauteur_max, nombre_de_niveaux, living_area_estimation, nombre_etages_estime). */
export interface PlaceCoproBuildingGroup {
  type: "Feature";
  geometry?: GeoJSON.Geometry | null;
  properties?: {
    surface?: number | null;
    hauteur_max?: number | null;
    nombre_de_niveaux?: number | null;
    living_area_estimation?: number | null;
    /** Nombre d'étages estimé (même calcul que /place : hauteur_max / 4) */
    nombre_etages_estime?: number | null;
  };
}

export interface PlaceCoproResponse {
  copros?: PlaceCoproFicheSimple[];
  nearbyCopro?: PlaceCoproNearbyFeature | null;
  buildingGroup?: PlaceCoproBuildingGroup | null;
}

/** Libellés des champs de la fiche copro (ordre d'affichage) */
const COPRO_FICHE_FIELDS: { key: string; label: string }[] = [
  { key: "adresse_de_reference", label: "Adresse de référence" },
  { key: "adresse_complementaire_1", label: "Adresse complémentaire 1" },
  { key: "adresse_complementaire_2", label: "Adresse complémentaire 2" },
  { key: "adresse_complementaire_3", label: "Adresse complémentaire 3" },
  { key: "nombre_d_adresses_complementaires", label: "Nombre d'adresses complémentaires" },
  { key: "date_de_la_derniere_maj", label: "Date de la dernière mise à jour" },
  { key: "type_de_syndic_benevole_professionnel_non_connu", label: "Type de syndic" },
  { key: "raison_sociale_du_representant_legal", label: "Raison sociale du représentant légal" },
  { key: "siret_du_representant_legal", label: "SIRET du représentant légal" },
  { key: "mandat_en_cours_dans_la_copropriete", label: "Mandat en cours dans la copropriété" },
  { key: "date_de_fin_du_dernier_mandat", label: "Date de fin du dernier mandat" },
  { key: "nom_d_usage_de_la_copropriete", label: "Nom d'usage de la copropriété" },
  { key: "date_du_reglement_de_copropriete", label: "Date du règlement de copropriété" },
  { key: "residence_service", label: "Résidence service" },
  { key: "syndicat_cooperatif", label: "Syndicat coopératif" },
  { key: "syndicat_principal_ou_syndicat_secondaire", label: "Syndicat principal ou secondaire" },
  { key: "si_secondaire_n_d_immatriculation_du_principal", label: "Si secondaire, n° immat. du principal" },
  { key: "nombre_total_de_lots", label: "Nombre total de lots" },
  { key: "nombre_total_de_lots_a_usage_d_habitation_de_bureaux_ou_de_comm", label: "Lots habitation / bureaux / commerce" },
  { key: "nombre_de_lots_a_usage_d_habitation", label: "Lots à usage d'habitation" },
  { key: "nombre_de_lots_de_stationnement", label: "Lots de stationnement" },
  { key: "periode_de_construction", label: "Période de construction" },
  { key: "nom_qp_2015", label: "Nom QPV 2015" },
  { key: "code_qp_2015", label: "Code QPV 2015" },
  { key: "nom_qp_2024", label: "Nom QPV 2024" },
  { key: "code_qp_2024", label: "Code QPV 2024" },
  { key: "copro_aidee", label: "Copropriété aidée" },
  { key: "copro_dans_acv", label: "Copro dans ACV" },
  { key: "copro_dans_pvd", label: "Copro dans PVD" },
  { key: "copro_dans_pdp", label: "Copro dans PDP" },
  { key: "code_de_pdp", label: "Code de PDP" },
  { key: "epci", label: "EPCI" },
  { key: "code_officiel_epci", label: "Code officiel EPCI" },
  { key: "nom_officiel_epci", label: "Nom officiel EPCI" },
];

const DATE_FICHE_KEYS = new Set([
  "date_de_la_derniere_maj",
  "date_de_fin_du_dernier_mandat",
  "date_du_reglement_de_copropriete",
]);

/** Libellés lisibles pour période de construction (année de construction) */
const PERIODE_CONSTRUCTION_LABELS: Record<string, string> = {
  AVANT_1949: "Avant 1949",
  DE_1949_A_1960: "De 1949 à 1960",
  DE_1961_A_1974: "De 1961 à 1974",
  DE_1975_A_1993: "De 1975 à 1993",
  DE_1994_A_2000: "De 1994 à 2000",
  DE_2001_A_2010: "De 2001 à 2010",
  A_COMPTER_DE_2011: "À compter de 2011",
};
const PERIODE_CONSTRUCTION_NA = new Set([
  "", "non connu", "non renseigné", "non_connue", "non_renseigné",
  "NON_CONNUE", "NON_CONNU", "NON_RENSEIGNÉ", "NON_RENSEIGNE",
]);

function formatFicheValue(v: unknown, key?: string): string | null {
  if (v == null) return null;
  if (typeof v === "object" && "numberLong" in (v as object)) return String((v as { numberLong: string }).numberLong);
  const raw = String(v).trim();
  if (key === "periode_de_construction") {
    const normalized = raw.toUpperCase().replace(/\s+/g, "_");
    if (PERIODE_CONSTRUCTION_NA.has(raw) || PERIODE_CONSTRUCTION_NA.has(normalized)) return "N/A";
    return PERIODE_CONSTRUCTION_LABELS[raw] ?? PERIODE_CONSTRUCTION_LABELS[normalized] ?? raw;
  }
  if (raw === "" || raw.toLowerCase() === "non connu") return null;
  if (key && DATE_FICHE_KEYS.has(key)) {
    const d = new Date(raw);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
    }
  }
  return raw;
}

const LABEL_COLUMN_WIDTH = 280;

function FieldList({ items }: { items: { label: string; value: string | null; key: string }[] }) {
  const filtered = items.filter((i) => i.value != null && i.value !== "");
  return (
    <Box component="ul" sx={{ margin: 0, paddingLeft: 0, listStyle: "none", "& li": { py: 0.4, display: "flex", alignItems: "flex-start" } }}>
      {filtered.map((item, idx) => (
        <li key={idx}>
          <Typography
            variant="body2"
            component="span"
            sx={{ minWidth: LABEL_COLUMN_WIDTH, flexShrink: 0, color: "#5c5c5c", fontWeight: 500 }}
          >
            {item.label}:
          </Typography>
          <Typography variant="body2" component="span" sx={{ flex: 1, minWidth: 0, color: "rgba(0,0,0,0.9)" }}>
            {item.value}
          </Typography>
        </li>
      ))}
    </Box>
  );
}

function SyndicPortfolioBanner({
  request,
  onViewPortfolio,
}: {
  request: CoproPortfolioOpenRequest;
  onViewPortfolio?: (request: CoproPortfolioOpenRequest) => void;
}) {
  const denomination = asPortfolioText(request.portfolio.societe?.denomination);
  const siren = formatSiren(request.portfolio.societe?.siren);
  const adresse = asPortfolioText(request.portfolio.etablissement?.adresse);
  const siege = isEtablissementSiege(request.portfolio.etablissement?.siege);
  const line1 = [denomination, siren].filter(Boolean).join(" · ");
  const line2 = [adresse, siege ? "siège" : ""].filter(Boolean).join(" · ");

  return (
    <Box
      sx={{
        my: 1.25,
        px: 1.5,
        py: 1,
        width: "100%",
        background: "#f8f9fa",
        borderRadius: "4px",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: 0.25,
      }}
    >
      <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: "#1976d2", lineHeight: 1.35 }}>
        {line1 || "—"}
      </Typography>
      {line2 && (
        <Typography variant="body2" sx={{ color: "#333", width: "100%" }}>
          {line2}
        </Typography>
      )}
      <Typography variant="body2" sx={{ color: "#333", fontWeight: 600, width: "100%" }}>
        {coprosGereesLabel(request.portfolio.totals?.mandatEnCours)}
      </Typography>
      <Button
        size="small"
        variant="contained"
        onClick={() => onViewPortfolio?.(request)}
        sx={{ mt: 0.75, textTransform: "none" }}
      >
        Voir le portefeuille
      </Button>
    </Box>
  );
}

interface CoproTabContentProps {
  placeCopro: PlaceCoproResponse | null | undefined;
  selectedCoproIndex: number;
  onSelectedCoproIndexChange: (index: number) => void;
  onViewPortfolio?: (request: CoproPortfolioOpenRequest) => void;
}

export default function CoproTabContent({
  placeCopro,
  selectedCoproIndex,
  onSelectedCoproIndexChange,
  onViewPortfolio,
}: CoproTabContentProps) {
  const copros = placeCopro?.copros ?? [];
  const nearbyCopro = placeCopro?.nearbyCopro ?? null;
  const hasDirectMatch = copros.length > 0;
  const itemsForList: Record<string, unknown>[] = [
    ...copros.map((c) => c as Record<string, unknown>),
    ...(nearbyCopro?.properties ? [nearbyCopro.properties] : []),
  ];
  const totalCount = itemsForList.length;
  const safeIndex = totalCount > 0 ? Math.min(selectedCoproIndex, totalCount - 1) : 0;

  useEffect(() => {
    if (safeIndex !== selectedCoproIndex) onSelectedCoproIndexChange(safeIndex);
  }, [safeIndex, selectedCoproIndex, onSelectedCoproIndexChange]);

  if (!placeCopro) {
    return (
      <Typography variant="body2" color="text.secondary">
        Aucune donnée (requête place-copro non disponible).
      </Typography>
    );
  }

  if (totalCount === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        Aucune copropriété trouvée pour la parcelle à cette adresse.
      </Typography>
    );
  }

  const selectedItem = itemsForList[safeIndex];
  const isNearby = hasDirectMatch ? safeIndex >= copros.length : true;
  const selectedFiche = selectedItem as PlaceCoproFicheSimple | undefined;
  const parcellesCadastre = selectedFiche?.parcelles_cadastre ?? [];
  const propsBuildingGroup = placeCopro?.buildingGroup?.properties;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      {/* Vignettes groupe de bâtiment : 4 au total (ordre comme Infos générales : Surface au sol, Surface habitable, Hauteur, Nombre d'étages) */}
      {placeCopro?.buildingGroup != null && (
        <Box className="info-section" sx={{ mb: 1.5 }}>
          <Typography className="info-section-title" sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#333", mb: 0.5 }}>
            Groupe de bâtiment
          </Typography>
          <div className="metrics-grid">
            <div className="metric-item">
              <div className="metric-label">
                <span className="metric-label-text">Surface au sol</span>
              </div>
              <div className="metric-value">
                {propsBuildingGroup?.surface != null
                  ? `${new Intl.NumberFormat("fr-FR").format(propsBuildingGroup.surface)} m²`
                  : "—"}
              </div>
            </div>
            <div className="metric-item">
              <div className="metric-label">
                <span className="metric-label-text">Surface habitable</span>
              </div>
              <div className="metric-value">
                {propsBuildingGroup?.living_area_estimation != null
                  ? `${new Intl.NumberFormat("fr-FR").format(propsBuildingGroup.living_area_estimation)} m²`
                  : "—"}
              </div>
            </div>
            <div className="metric-item">
              <div className="metric-label">
                <span className="metric-label-text">Hauteur max.</span>
              </div>
              <div className="metric-value">
                {propsBuildingGroup?.hauteur_max != null
                  ? `${new Intl.NumberFormat("fr-FR").format(propsBuildingGroup.hauteur_max)} m`
                  : "—"}
              </div>
            </div>
            <div className="metric-item">
              <div className="metric-label">
                <span className="metric-label-text">Nombre d'étages</span>
              </div>
              <div className="metric-value">
                {propsBuildingGroup?.nombre_etages_estime != null
                  ? new Intl.NumberFormat("fr-FR").format(propsBuildingGroup.nombre_etages_estime)
                  : "—"}
              </div>
            </div>
          </div>
        </Box>
      )}
      <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
        {/* Liste des copros à gauche avec sections */}
        <Paper variant="outlined" sx={{ width: 260, flexShrink: 0, overflow: "auto", mr: 1 }}>
          <List dense disablePadding>
            {copros.length > 0 && (
              <>
                <Typography variant="overline" color="text.secondary" sx={{ px: 1.5, pt: 1, pb: 0.5, display: "block" }}>
                  Correspondance parcelle
                </Typography>
                {copros.map((copro, i) => {
                  const immat = formatFicheValue((copro as Record<string, unknown>).numero_d_immatriculation) ?? "";
                  const label = immat ? immat : `Copropriété ${i + 1}`;
                  return (
                    <ListItemButton
                      key={`direct-${i}`}
                      selected={i === safeIndex}
                      onClick={() => onSelectedCoproIndexChange(i)}
                    >
                      <ListItemText primary={label} primaryTypographyProps={{ variant: "body2" }} />
                    </ListItemButton>
                  );
                })}
              </>
            )}
            {nearbyCopro?.properties && (
              <>
                <Typography variant="overline" color="text.secondary" sx={{ px: 1.5, pt: copros.length > 0 ? 1.5 : 1, pb: 0.5, display: "block" }}>
                  À proximité
                </Typography>
                <ListItemButton
                  key="nearby"
                  selected={isNearby}
                  onClick={() => onSelectedCoproIndexChange(copros.length)}
                >
                  <ListItemText
                    primary={(() => {
                      const props = nearbyCopro.properties as Record<string, unknown>;
                      const immat = formatFicheValue(props.numero_d_immatriculation) ?? "";
                      const dist = props.distance as { calculated?: number } | undefined;
                      const m = dist?.calculated != null ? Math.round(dist.calculated) : null;
                      const distStr = m != null ? ` (${m} m)` : "";
                      return immat ? `${immat}${distStr}` : (m != null ? `À proximité${distStr}` : "À proximité");
                    })()}
                    primaryTypographyProps={{ variant: "body2" }}
                  />
                </ListItemButton>
              </>
            )}
          </List>
        </Paper>

        {/* Données de la copro sélectionnée à droite */}
        <Paper variant="outlined" sx={{ flex: 1, overflow: "auto", p: 1.5 }}>
          <Typography variant="subtitle2" color="primary" gutterBottom>
            Données copropriété
          </Typography>
          {selectedItem && (
            <>
              {(() => {
                const nombreAddr = selectedItem.nombre_d_adresses_complementaires;
                const n = typeof nombreAddr === "number" ? nombreAddr : (formatFicheValue(nombreAddr, undefined) ? Number(formatFicheValue(nombreAddr, undefined)) : undefined);
                const ficheItems = COPRO_FICHE_FIELDS.filter(({ key }) => {
                  if (key === "long" || key === "lat") return false;
                  if (key === "adresse_complementaire_1") return n == null || n >= 1;
                  if (key === "adresse_complementaire_2") return n == null || n >= 2;
                  if (key === "adresse_complementaire_3") return n == null || n >= 3;
                  return true;
                }).map(({ key, label }) => ({
                  key,
                  label,
                  value: formatFicheValue(selectedItem[key], key),
                }));
                const siretFieldIndex = ficheItems.findIndex((item) => item.key === "siret_du_representant_legal");
                const ficheItemsBeforePortfolio = siretFieldIndex >= 0 ? ficheItems.slice(0, siretFieldIndex + 1) : ficheItems;
                const ficheItemsAfterPortfolio = siretFieldIndex >= 0 ? ficheItems.slice(siretFieldIndex + 1) : [];
                const portfolioRequest = portfolioContextFromFiche(selectedItem);
                return (
                  <>
                    <FieldList items={ficheItemsBeforePortfolio} />
                    {portfolioRequest && (
                      <SyndicPortfolioBanner request={portfolioRequest} onViewPortfolio={onViewPortfolio} />
                    )}
                    <FieldList items={ficheItemsAfterPortfolio} />
                  </>
                );
              })()}
              <Box sx={{ mt: 1.5, pt: 1, borderTop: 1, borderColor: "divider" }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                  Parcelles cadastrales
                </Typography>
                {parcellesCadastre.length > 0 ? (
                  <Box component="ul" sx={{ margin: 0, paddingLeft: 2, "& li": { py: 0.25 } }}>
                    {parcellesCadastre.map((p, idx) => (
                      <li key={p.id ?? idx}>
                        <Typography variant="body2" component="span">{p.id}</Typography>
                      </li>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Aucune parcelle renseignée
                  </Typography>
                )}
              </Box>
            </>
          )}
        </Paper>
      </Box>
    </Box>
  );
}
